import * as React from "react";
import {
  Input as ChakraInput,
  InputGroup,
  IconButton,
  type InputProps as ChakraInputProps,
  useControllableState,
} from "@chakra-ui/react";
import { LuEye, LuEyeOff } from "react-icons/lu";

export interface InputPasswordProps extends ChakraInputProps {
  defaultVisible?: boolean;
  visible?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  visibilityIcon?: { on: React.ReactNode; off: React.ReactNode };
  groupProps?: Omit<React.ComponentProps<typeof InputGroup>, "children">;
  startElement?: React.ReactNode;
}

export const InputPassword = React.forwardRef<HTMLInputElement, InputPasswordProps>(
  function InputPassword(props, ref) {
    const {
      defaultVisible = false,
      visible: visibleProp,
      onVisibleChange,
      visibilityIcon = {
        on: <LuEye />,
        off: <LuEyeOff />,
      },
      groupProps,
      startElement,
      ...inputProps
    } = props;

    const [visible, setVisible] = useControllableState({
      value: visibleProp,
      defaultValue: defaultVisible,
      onChange: onVisibleChange,
    });

    const toggleVisibility = React.useCallback(() => setVisible((v) => !v), [setVisible]);

    return (
      <InputGroup
        startElement={startElement}
        endElement={
          <IconButton
            aria-label={visible ? "Hide password" : "Show password"}
            variant="ghost"
            size="md"
            color="gray.600"
            bg="transparent"
            _hover={{ bg: "none" }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={toggleVisibility}
            p="0"
            minW="auto"
            w="auto"
            h="auto"
          >
            {visible ? visibilityIcon.off : visibilityIcon.on}
          </IconButton>
        }
        {...groupProps}
      >
        <ChakraInput
          ref={ref}
          type={visible ? "text" : "password"}
          variant={inputProps.variant ?? "outline"}
          size={inputProps.size ?? "md"}
          {...inputProps}
        />
      </InputGroup>
    );
  }
);
